/**
 * useHandleRegionListModal Hook
 * Handles opening/closing region list modal for web
 * @see mobile: module-customer/src/modules/addresses/hooks/use-handle-region-list-bottom-sheet.ts
 *
 * Note: This is the web adaptation of the mobile bottom sheet hook.
 * On mobile, this uses BottomSheet component.
 * On web, this uses Modal component for the same functionality.
 */

'use client';

import { useCallback, useState, type ReactNode } from 'react';

export interface ModalExtraParamsProps {
    enableDynamicSizing?: boolean;
    scrollEnabled?: boolean;
    height?: string;
    customHeader?: ReactNode;
}

export interface UseHandleRegionListModalProps {
    setInitialRegionStore: () => void;
}

export interface UseHandleRegionListModalReturn {
    isModalOpen: boolean;
    modalContent: ReactNode | null;
    modalHeader: ReactNode | null;
    onOpenRegionsDirectory: (view: ReactNode, header: ReactNode) => void;
    onOpenDistrictsDirectory: (view: ReactNode, header: ReactNode) => void;
    onOpenWardsDirectory: (view: ReactNode, header: ReactNode) => void;
    onCloseModal: () => void;
    modalProps: ModalExtraParamsProps;
}

export const useHandleRegionListModal = ({
    setInitialRegionStore,
}: UseHandleRegionListModalProps): UseHandleRegionListModalReturn => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<ReactNode | null>(null);
    const [modalHeader, setModalHeader] = useState<ReactNode | null>(null);

    const modalProps: ModalExtraParamsProps = {
        enableDynamicSizing: false,
        scrollEnabled: false,
        height: '75%',
    };

    const handleOpenModal = useCallback((view: ReactNode, header: ReactNode) => {
        setModalContent(view);
        setModalHeader(header);
        setIsModalOpen(true);
    }, []);

    const handleOpenRegionsDirectory = useCallback(
        (view: ReactNode, header: ReactNode) => {
            handleOpenModal(view, header);
        },
        [handleOpenModal]
    );

    const handleOpenDistrictsDirectory = useCallback(
        (view: ReactNode, header: ReactNode) => {
            handleOpenModal(view, header);
        },
        [handleOpenModal]
    );

    const handleOpenWardsDirectory = useCallback(
        (view: ReactNode, header: ReactNode) => {
            handleOpenModal(view, header);
        },
        [handleOpenModal]
    );

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setModalContent(null);
        setModalHeader(null);
        setInitialRegionStore();
    }, [setInitialRegionStore]);

    return {
        isModalOpen,
        modalContent,
        modalHeader,
        onOpenRegionsDirectory: handleOpenRegionsDirectory,
        onOpenDistrictsDirectory: handleOpenDistrictsDirectory,
        onOpenWardsDirectory: handleOpenWardsDirectory,
        onCloseModal: handleCloseModal,
        modalProps,
    };
};
